<template>
    <div class="grid-container content-padding" v-if="currentUser">
        <div class="grid-x">
            <div class="food-side-content">
                <page-header title="My account"></page-header>
            </div>
            <div class="cell account-container large-5 large-offset-5">
                <div class="align-middle grid-x align-justify account-page-header">
                    <div class="cell shrink">
                        <p class="display-4 semi-bold margin-bottom-0">{{ currentUser.name }}</p>
                    </div>
                    <div class="cell shrink">
                        <a @click.prevent="logout" class="button logout-button"><span class="logout-icon" v-html="Icon.LOGOUT"></span>Logout</a>
                    </div>
                </div>
                <sliding-tab-menu :tabs="tabs" @select-tab="updateSelectedTab">
                    <template v-slot:contact-info>
                        <text-input
                            label="Full name"
                            type="text"
                            autocomplete="name"
                            :value="userValue('name')"
                            @blur="updateValue('name', $event.target.value)"
                            :error-message="nameError"
                        ></text-input>
                        <text-input
                            label="Email"
                            type="email"
                            autocomplete="email"
                            :value="userValue('email')"
                            :error-message="emailError"
                            @blur="updateValue('email', $event.target.value)"
                        ></text-input>
                         <phone-input
                            :required="true"
                            name="tel"
                            :value="userValue('phone')"
                            placeholder="Phone"
                            :preferred-countries="['US', 'KY', 'CA', 'GB']"
                            default-country="KY"
                            mode="international"
                            @validate="validatePhone"
                            :error-message="phoneError"
                        >
                        </phone-input>
                        <text-input
                            label="Membership number (optional)"
                            type="number"
                            name="membership_number"
                            :value="userValue('membership_number')"
                            @blur="updateValue('membership_number', $event.target.value)"
                            :error-message="membershipError">
                        </text-input>
                        <address-field v-if="mapsApiKey"
                            country="ky"
                            :apiKey="mapsApiKey"
                            :deliveryAddress="userValue('default_address')"
                            :storePlaceId="storePlaceId"
                            :maxDeliveryDistance="maxDeliveryDistance"
                            @onPredictionUpdate="updateValue('address_place_id', $event)"
                            :error-message="addressError"
                            @change="updateValue('default_address', $event)">
                        </address-field>
                        <text-input
                            label="Business Name / Residence Name / Unit number"
                            type="text"
                            autocomplete="address-line2"
                            :value="userValue('business_name')"
                            @blur="updateValue('business_name', $event.target.value)"
                        ></text-input>
                        <text-input
                            placeholder="Delivery Instructions - e.g: Floor / Ring bell (optional)"
                            :textarea="true"
                            type="text"
                            autocomplete="off"
                            :value="userValue('delivery_instructions')"
                            @blur="updateValue('delivery_instructions', $event.target.value)"
                        ></text-input>
                        <!-- <div class="radio-list">
                            <p class="display-large color-dark-gray margin-0">Notification preferences</p>
                            <ul>
                                <li>
                                    <checkbox
                                        label="SMS + Email"
                                        class="radio-list-option"
                                        :label-left="true"
                                        :radio="true"
                                        :checked="notifyBy === NotificationChannel.EMAIL_SMS"
                                        @change="updateValue('notify_by', NotificationChannel.EMAIL_SMS)"
                                    ></checkbox>
                                </li>
                                <li>
                                    <checkbox
                                        label="SMS only"
                                        class="radio-list-option"
                                        :label-left="true"
                                        :radio="true"
                                        :checked="notifyBy === NotificationChannel.SMS_ONLY"
                                        @change="updateValue('notify_by', NotificationChannel.SMS_ONLY)"
                                    ></checkbox>
                                </li>
                                <li>
                                    <checkbox
                                        label="Email only"
                                        class="radio-list-option"
                                        :label-left="true"
                                        :radio="true"
                                        :checked="notifyBy === NotificationChannel.EMAIL_ONLY"
                                        @change="updateValue('notify_by', NotificationChannel.EMAIL_ONLY)"
                                    ></checkbox>
                                </li>
                            </ul>
                        </div> -->
                    </template>
                    <template v-slot:password>
                        <text-input
                            label="Current password"
                            type="password"
                            :value="oldPassword"
                            @change="handleOldPasswInput"
                        ></text-input>
                        <text-input
                            label="New password"
                            type="password"
                            :value="newPassword"
                            @change="handleNewPasswInput"
                        ></text-input>
                    </template>
                    <template v-slot:payment-methods>
                        <div class="payment-section">
                            <h3 class="payment-section__title">Payment</h3>

                            <!-- Loading state -->
                            <div v-if="cardsLoading" class="payment-loading">
                                <p class="display-small">Loading your cards…</p>
                            </div>

                            <div v-else class="payment-list">
                                <!-- Empty state -->
                                <div v-if="savedCards.length === 0 && !showAddCardForm" class="payment-empty">
                                    <p class="display-small">You have no saved payment methods.</p>
                                </div>

                                <!-- Cards list -->
                                <div
                                    v-for="card in savedCards"
                                    :key="card.id"
                                    class="payment-row"
                                    :class="{ 'payment-row--selected': card.is_default }"
                                    @click="!card.is_default && setCardDefault(card.id)"
                                >
                                    <div class="payment-row__radio">
                                        <span v-if="card.is_default" class="payment-row__check">
                                            <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                                                <circle cx="11" cy="11" r="11" fill="var(--primary)"/>
                                                <path d="M6.5 11.5L9.5 14.5L15.5 8" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                            </svg>
                                        </span>
                                        <span v-else class="payment-row__circle"></span>
                                    </div>
                                    <div class="payment-row__info">
                                        <p class="payment-row__name" :class="{ 'semi-bold': card.is_default }">{{ card.brand }} *{{ card.last_four }}</p>
                                        <p class="payment-row__expiry">Expires {{ card.exp_month }}/{{ card.exp_year.slice(-2) }}</p>
                                    </div>
                                    <div class="payment-row__brand">
                                        <span v-html="brandBadge(card.brand)"></span>
                                    </div>
                                    <button class="payment-row__delete" @click.stop="removeCard(card.id)" title="Remove card">
                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                            <path d="M2 4h12M5.333 4V2.667a1.333 1.333 0 011.334-1.334h2.666a1.333 1.333 0 011.334 1.334V4m2 0v9.333a1.333 1.333 0 01-1.334 1.334H4.667a1.333 1.333 0 01-1.334-1.334V4h9.334z" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                                        </svg>
                                    </button>
                                </div>

                                <!-- New card row -->
                                <div
                                    class="payment-row payment-row--new"
                                    :class="{ 'payment-row--selected': showAddCardForm }"
                                    @click="showAddCardForm = true"
                                >
                                    <div class="payment-row__radio">
                                        <span v-if="showAddCardForm" class="payment-row__check">
                                            <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                                                <circle cx="11" cy="11" r="11" fill="var(--primary)"/>
                                                <path d="M6.5 11.5L9.5 14.5L15.5 8" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                            </svg>
                                        </span>
                                        <span v-else class="payment-row__circle"></span>
                                    </div>
                                    <div class="payment-row__info">
                                        <p class="payment-row__name">New card</p>
                                    </div>
                                    <div class="payment-row__brand">
                                        <svg width="28" height="20" viewBox="0 0 28 20" fill="none">
                                            <rect x="0.5" y="0.5" width="27" height="19" rx="2.5" stroke="#ccc"/>
                                            <rect x="3" y="4" width="22" height="3" rx="1" fill="#ccc"/>
                                            <rect x="3" y="10" width="10" height="2" rx="1" fill="#ddd"/>
                                            <rect x="3" y="14" width="6" height="2" rx="1" fill="#ddd"/>
                                        </svg>
                                    </div>
                                </div>

                                <!-- Add card form -->
                                <div v-if="showAddCardForm" class="add-card-form">
                                    <credit-card-input
                                        label="Card number"
                                        name="ccNumber"
                                        :value="cardForm.cc_number"
                                        @change="cardForm.cc_number = $event"
                                        :error-message="cardErrors.cc_number"
                                        :required="true"
                                    ></credit-card-input>
                                    <div class="grid-x grid-padding-x">
                                        <div class="cell auto">
                                            <expiration-date
                                                label="Exp date"
                                                :value="cardForm.exp_date"
                                                @change="cardForm.exp_date = $event"
                                                :error-message="cardErrors.exp_date"
                                                :required="true"
                                            ></expiration-date>
                                        </div>
                                        <div class="cell auto">
                                            <text-input
                                                label="CVV"
                                                type="password"
                                                name="cvv"
                                                :value="cardForm.cvv"
                                                @change="cardForm.cvv = $event"
                                                :error-message="cardErrors.cvv"
                                                :required="true"
                                            ></text-input>
                                        </div>
                                    </div>
                                    <div class="add-card-form__buttons">
                                        <button class="button" @click="submitCard" :disabled="cardSaving">
                                            {{ cardSaving ? 'Saving…' : 'Save card' }}
                                        </button>
                                        <button class="button ghost" @click="cancelCardForm">Cancel</button>
                                    </div>
                                    <Alert :type="AlertType.ERROR" v-if="cardFormError" title="Error" style="margin-top:10px;">{{ cardFormError }}</Alert>
                                </div>
                            </div>

                            <Alert :type="AlertType.SUCCESS" v-if="cardSuccessMessage" title="Success" style="margin-top:15px;">{{ cardSuccessMessage }}</Alert>
                        </div>
                    </template>
                </sliding-tab-menu>
                <Alert :type="AlertType.SUCCESS" v-if="profileHasBeenUpdated" title="Profile Update" style="margin-bottom:15px;">Your information have been successfully saved.</Alert>
                <button v-if="profileUpdate" @click="save" :disabled="!canSave || loading" class="button save-button">Save</button>
                <div class="cell">
                    <p class="display-4 semi-bold">Account privacy</p>
                    <a @click.prevent="downloadData" class="cell button small" :disabled="hasDownloadedData">Download your data</a>
                    <a @click.prevent="anonymizeData" class="cell button ghost small" :disabled="hasDeletedData">Delete your account</a>
                    <alert v-if="privacyResponse" :type="privacyAlertType" :title="privacyRequestTitle">{{ privacyResponse }}</alert>
                </div>
            </div>
        </div>
    </div>
</template>
<script lang="ts">
import Vue from 'vue'
import Component from 'vue-class-component'
import { getModule } from 'vuex-module-decorators';
import UserModule from '@/stores/modules/user';
import User from '@/models/user';
import TextInput from '@/components/common/forms/TextInput.vue';
import SlidingTabMenu from '@/components/common/SlidingTabMenu.vue';
import AddressField from '@/components/common/forms/AddressField.vue';
import PhoneInput from '@/components/common/forms/PhoneInput.vue';
import Checkbox from '@/components/common/ui/Checkbox.vue';
import { NotificationChannel } from '@/models/user';
import CartModule from '@/stores/modules/cart';
import PageHeader from '@/components/front/ui/PageHeader.vue';
import Alert from '@/components/common/ui/Alert.vue';
import { AlertType } from '@/utils/types';
import { Icon } from '@/front/icons';
import Settings, { SettingNamespace } from '../../utils/settings';
import { getSavedCards, storeSavedCard, deleteSavedCard, SavedCard } from '@/models/saved-card';
import CreditCardInput from '@/components/front/cart/CreditCardInput.vue';
import ExpirationDate from '@/components/front/cart/ExpirationDate.vue';
import axios from 'axios';

@Component({
    components: { TextInput, SlidingTabMenu, Checkbox, PageHeader, AddressField, PhoneInput, Alert, CreditCardInput, ExpirationDate }
})
export default class AccountPage extends Vue {
    userModule!: UserModule;
    cartModule!: CartModule;
    selectedTab: number = 0;
    oldPassword: string = '';
    newPassword: string = '';
    emailError: string = '';
    phoneError: string = '';
    addressError: string = '';
    nameError: string = '';
    membershipError: string = '';
    privacyResponse: string = '';
    privacyResponseSuccess: boolean = false;
    hasDownloadedData: boolean = false;
    hasDeletedData: boolean = false;
    mapsApiKey: string = '';
    storePlaceId: string = '';
    maxDeliveryDistance: number = 0;
    loading: boolean = false;
    profileHasBeenUpdated: boolean = false;

    // Payment methods tab
    cardsLoading: boolean = false;
    showAddCardForm: boolean = false;
    cardSaving: boolean = false;
    cardFormError: string = '';
    cardSuccessMessage: string = '';
    cardForm = { cc_number: '', exp_date: '', cvv: '' };
    cardErrors = { cc_number: '', exp_date: '', cvv: '' };

    async created() {
        this.userModule = getModule(UserModule, this.$store);
        this.cartModule = getModule(CartModule, this.$store);
        const settings = await Settings.getInstance();
        this.storePlaceId = settings.get(SettingNamespace.ORDERS, 'store_place_id')?.value;
        this.maxDeliveryDistance = parseFloat(settings.get(SettingNamespace.ORDERS, 'max_distance')?.value);
        this.mapsApiKey = settings.get(SettingNamespace.GENERAL, 'maps_api_key')?.value;
    }

    mounted() {
        if (!this.currentUser) {
            this.$router.push({name: 'food'}).catch(err => {
                if (err.name !== 'NavigationDuplicated') {
                    console.error('Navigation error:', err);
                }
            });
        }
    }

    get savedCards(): SavedCard[] {
        return this.currentUser?.saved_cards || [];
    }

    get AlertType() {
        return AlertType;
    }

    public async downloadData() {
        this.hasDownloadedData = true;
        const response = await this.userModule.downloadData();
        this.privacyResponseSuccess = response.success;
        this.privacyResponse = response.content.message;
        this.hasDownloadedData = false;
    }

    public async anonymizeData() {
        this.hasDeletedData = true;
        const response = await this.userModule.anonymizeData();
        this.privacyResponseSuccess = response.success;
        this.privacyResponse = response.content.message;
        if (this.privacyResponseSuccess) {
            window.setTimeout(() => {
                this.userModule.logout();
                this.$router.push({name: 'food'}).catch(err => {
                    if (err.name !== 'NavigationDuplicated') {
                        console.error('Navigation error:', err);
                    }
                });
            }, 2 * 1000)
        }
    }

    async validateField(key: string, value: any) {
        const creditCardRegex = /(?:^|[^0-9])(?:\d[ -]*?){13,16}(?:$|[^0-9])/g;
        switch (key) {
            case 'name':
                if (value === '') {
                    this.nameError = 'Please enter a valid name.';
                    return false;
                }
                if (/\d/.test(value)) {
                    this.nameError = 'Please enter a valid name. No numbers allowed.';
                    return false;
                }
                this.nameError = '';
                return true;
            case 'membership_number':
                const isNumberValid = value.length === 13 && /^\d+$/.test(value);
                if (value.length > 0 && !isNumberValid) {
                    this.membershipError = 'The membership number must contains 13 digits.';
                    return false;
                }
                this.membershipError = '';
                return true;

            case 'email':
                const isEmailValid = await this.isEmailValid(value);
                if (creditCardRegex.test(value)) {
                    return false;
                }
                if (isEmailValid) {
                    this.emailError = '';
                    return true;
                }
                this.emailError = 'Please enter a valid email.';
                return false;
            default:
                if (creditCardRegex.test(value)) {
                    value = value.replace(creditCardRegex, '');
                    return false;
                }
            return true;
        }
    }

    public async updateValue(key: string, value: any) {
        const isValid = await this.validateField(key, value);
        if (isValid) {
            this.userModule.setUserValue({key, value});
        }
    }

    public logout(): void {
        this.userModule.logout();
        this.cartModule.clearProducts();
        this.$router.push({name: 'food'}).catch(err => {
            if (err.name !== 'NavigationDuplicated') {
                console.error('Navigation error:', err);
            }
        });
    }

    get canSave() {
        return !this.nameError && !this.phoneError && !this.addressError && !this.emailError;
    }

    get notifyBy() {
        if (this.userModule.draftUser) {
            return this.userModule.draftUser.notify_by;
        }
    }

    get NotificationChannel() {
        return NotificationChannel;
    }

    async save() {
        this.loading = true;
        await this.userModule.saveDraft();
        if (this.oldPassword.length) {
            if (this.currentUser) {
                await this.currentUser.changePassword(this.oldPassword, this.newPassword);
            }
        }
        this.profileHasBeenUpdated = true;
        setTimeout(() => {
            this.profileHasBeenUpdated = false;
        }, 3000);
        this.loading = false;
    }

    get userDraft() {
        return this.userModule.draftUser;
    }

    get profileUpdate() {
        if (this.currentUser && this.userDraft) {
            return this.currentUser.name !== this.userDraft.name || this.currentUser.email !== this.userDraft.email || this.currentUser.phone !== this.userDraft.phone || this.currentUser.default_address !== this.userDraft.default_address || this.currentUser.business_name !== this.userDraft.business_name || this.currentUser.delivery_instructions !== this.userDraft.delivery_instructions || this.currentUser.notify_by !== this.userDraft.notify_by || this.currentUser.membership_number !== this.userDraft.membership_number;
        }
    }

    async isEmailValid(value: string) {
        const emailRegex = /\S+@\S+\.\S+/;
        if (!emailRegex.test(value)) {
            this.emailError = 'Please enter a valid email.';
            return false;
        }
        const user = await User.current();
        const emailExists = await User.exists(value);
        if (user!.email !== value && emailExists.success) {
            this.emailError = 'This e-mail is already used. Please update your e-mail.';
            return false;
        }
        return true;
    }

    validatePhone(phoneObject: any) {
        if (phoneObject.formatted !== '' && !phoneObject.valid)  {
            this.phoneError = 'Please enter a valid phone number.';
            return false;
        }
        this.phoneError = '';
        this.updateValue('phone', phoneObject.formatted);
        return true;
    }

    handleOldPasswInput(value: string) {
        this.oldPassword = value;
    }

    handleNewPasswInput(value: string) {
        this.newPassword = value;
    }



    get currentUser(): User | null {
        return this.userModule.currentUser;
    }

    get draftUser(): User | null {
        return this.userModule.draftUser;
    }

    get privacyAlertType() {
        return (this.privacyResponseSuccess) ? AlertType.SUCCESS : AlertType.ERROR;
    }

    get privacyRequestTitle() {
        return (this.privacyResponseSuccess) ? "Request successful" : "Something went wrong";
    }

    get userValue() {
        return (key: string) => {
            if (this.draftUser) {
                return this.draftUser[key];
            }
            return '';
        }
    }

    get tabs() {
        if (this.currentUser) {
            const t = [{key: 'contact-info', label: 'Contact info'}];
            if (!this.currentUser.provider) {
                t.push({key: 'password', label: 'Password'});
            }
            t.push({key: 'payment-methods', label: 'Payment Methods'});
            return t;
        }
    }

    async updateSelectedTab(tab: number) {
        this.selectedTab = tab;
    }

    brandIcon(brand: string): string {
        const b = brand.toLowerCase();
        if (b === 'visa') return '<strong style="color:#1a1f71;font-style:italic;">VISA</strong>';
        if (b === 'mastercard' || b === 'mc') return '<span style="color:#eb001b;">●</span><span style="color:#f79e1b;">●</span>';
        if (b === 'amex' || b === 'american express') return '<strong style="color:#006fcf;">AMEX</strong>';
        if (b === 'discover') return '<strong style="color:#ff6600;">DISC</strong>';
        return '<strong>' + brand.toUpperCase() + '</strong>';
    }

    brandBadge(brand: string): string {
        const b = brand.toLowerCase();
        if (b === 'visa') {
            return '<div class="brand-badge"><strong style="color:#1a1f71;font-size:14px;font-style:italic;">VISA</strong></div>';
        }
        if (b === 'mastercard' || b === 'mc') {
            return '<div class="brand-badge"><svg width="30" height="20" viewBox="0 0 30 20"><circle cx="11" cy="10" r="8" fill="#eb001b" opacity="0.9"/><circle cx="19" cy="10" r="8" fill="#f79e1b" opacity="0.9"/><path d="M15 3.8a8 8 0 010 12.4 8 8 0 000-12.4z" fill="#ff5f00"/></svg></div>';
        }
        if (b === 'amex' || b === 'american express') {
            return '<div class="brand-badge" style="background:#006fcf;color:#fff;font-size:9px;">AMEX</div>';
        }
        if (b === 'discover') {
            return '<div class="brand-badge" style="color:#ff6600;">DISCOVER</div>';
        }
        return '<div class="brand-badge">' + brand.toUpperCase() + '</div>';
    }

    validateCardForm(): boolean {
        let valid = true;
        this.cardErrors = { cc_number: '', exp_date: '', cvv: '' };

        if (!this.cardForm.cc_number || this.cardForm.cc_number.replace(/\s/g, '').length < 13) {
            this.cardErrors.cc_number = 'Please enter a valid card number.';
            valid = false;
        }
        if (!this.cardForm.exp_date || !this.cardForm.exp_date.includes('/')) {
            this.cardErrors.exp_date = 'Please enter a valid expiration date.';
            valid = false;
        }
        if (!this.cardForm.cvv || this.cardForm.cvv.length < 3) {
            this.cardErrors.cvv = 'Please enter a valid CVV.';
            valid = false;
        }
        return valid;
    }

    async submitCard() {
        this.cardFormError = '';
        this.cardSuccessMessage = '';
        if (!this.validateCardForm()) return;

        this.cardSaving = true;
        const result = await storeSavedCard(this.cardForm);
        this.cardSaving = false;

        if (result.success) {
            this.showAddCardForm = false;
            this.cardForm = { cc_number: '', exp_date: '', cvv: '' };
            this.cardSuccessMessage = 'Card saved successfully.';
            await this.userModule.fetchUser();
            setTimeout(() => { this.cardSuccessMessage = ''; }, 3000);
        } else {
            this.cardFormError = result.message || 'Failed to save card.';
        }
    }

    async removeCard(id: number) {
        this.cardSuccessMessage = '';
        const ok = await deleteSavedCard(id);
        if (ok) {
            this.cardSuccessMessage = 'Card removed.';
            await this.userModule.fetchUser();
            setTimeout(() => { this.cardSuccessMessage = ''; }, 3000);
        }
    }

    async setCardDefault(id: number) {
        try {
            const url = (window as any).route('api.saved_cards.set_default', { id });
            await axios.patch(url, {}, {
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
            });
            await this.userModule.fetchUser();
        } catch {
            await this.userModule.fetchUser();
        }
    }

    cancelCardForm() {
        this.showAddCardForm = false;
        this.cardForm = { cc_number: '', exp_date: '', cvv: '' };
        this.cardErrors = { cc_number: '', exp_date: '', cvv: '' };
        this.cardFormError = '';
    }

    get Icon() {
        return Icon;
    }
}
</script>
<style lang="scss" scoped>
@import '~foundation-sites/scss/foundation';
@import '../../../sass/front/settings';

.account-container {
    @include breakpoint(large) {
        padding: 50px 30px 150px;
    }
}

.account-page-header {
    padding: 25px 0px;
}

.save-button {
    @include breakpoint(medium down) {
        position: fixed;
        bottom: 81px;
        left: 0;
        right: 0;
        width: 100%;
        border-radius: 0px;
        margin: 0;
    }
}

.logout-button {
    display: flex;
    align-items: center;
    margin: 0;

    .logout-icon {
        height: 18px;
        margin-right: 10px;
    }
}

.account-error-message {
    color: var(--alert);
}

// Payment Methods tab styles
.payment-section {
    &__title {
        font-size: 18px;
        font-weight: 600;
        margin: 0 0 8px;
    }
}

.payment-list {
    border-top: 1px solid var(--light-gray, #eee);
}

.payment-row {
    display: flex;
    align-items: center;
    padding: 18px 0;
    border-bottom: 1px solid var(--light-gray, #eee);
    cursor: pointer;
    transition: background-color 0.15s;

    &:hover {
        background-color: rgba(0, 0, 0, 0.015);
    }

    &--selected {
        cursor: default;
    }

    &__radio {
        flex-shrink: 0;
        width: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    &__circle {
        display: block;
        width: 22px;
        height: 22px;
        border-radius: 50%;
        border: 2px solid var(--battleship-gray, #999);
    }

    &__check {
        display: flex;
    }

    &__info {
        flex: 1;
        min-width: 0;
        padding-left: 12px;
    }

    &__name {
        font-size: 15px;
        margin: 0;
        line-height: 1.3;
    }

    &__expiry {
        font-size: 13px;
        color: var(--battleship-gray, #888);
        margin: 2px 0 0;
    }

    &__brand {
        flex-shrink: 0;
        margin-left: auto;
        padding-left: 12px;
        display: flex;
        align-items: center;
    }

    &__delete {
        flex-shrink: 0;
        margin-left: 8px;
        background: none;
        border: none;
        color: var(--battleship-gray, #999);
        cursor: pointer;
        padding: 4px;
        border-radius: 4px;
        transition: color 0.15s, background-color 0.15s;
        display: flex;
        align-items: center;

        &:hover {
            color: var(--alert, #d9534f);
            background-color: rgba(217, 83, 79, 0.08);
        }
    }
}

.brand-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 52px;
    height: 34px;
    border: 1px solid var(--light-gray, #eee);
    border-radius: 6px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.5px;
}

.add-card-form {
    padding: 16px 0 8px;

    &__buttons {
        display: flex;
        gap: 12px;
        margin-top: 10px;

        .button:first-child {
            flex: 1;
        }
    }
}

.payment-empty,
.payment-loading {
    padding: 30px 0;
    text-align: center;
}
</style>

